import { SoftwarePackage } from "domain/softwarePackages";

const PRODUCT_TAG_PATTERN = /`product:([^:`]+):([^`]*)`/g;

const getGroupName = (softwarePackage: SoftwarePackage): string =>
    softwarePackage.bmpHomeGroup || softwarePackage.productName;

const PRODUCT_KEY_TO_PRODUCT_NAMES: Record<string, string[]> = {
    bde: [
        "Blancco Drive Eraser - Enterprise Subscription Edition",
        "Blancco Drive Eraser - Enterprise Volume Edition",
        "Blancco Drive Eraser - Volume Edition",
    ],
    bde_arm: [
        "Blancco Drive Eraser - Enterprise Volume Edition (for ARM Servers)",
        "Blancco Drive Eraser - Volume Edition (for ARM Servers)",
    ],
    bde_mac: ["Blancco Drive Eraser for Mac"],
    bdect: ["Blancco Drive Eraser Configuration Tool"],
    bfe: [
        "Blancco File Eraser - Home Edition",
        "Blancco File Eraser - Data Center Edition",
        "Blancco File Eraser - Enterprise Edition",
    ],
    blun: [
        "Blancco LUN Eraser (for Windows) per GB",
        "Blancco LUN Eraser (for Windows) per HDD",
        "Blancco LUN Eraser (for UNIX) per HDD",
    ],
    bmde: ["Blancco Mobile Diagnostics and Erasure"],
    bmwe: ["Blancco Mobile Workspace Edition"],
    brme: ["Blancco Removable Media Eraser - Enterprise Edition", "Blancco Removable Media Eraser"],
    busb: ["Blancco USB Creator"],

    wineraser: [
        "Blancco WinEraser Volume Edition",
        "Blancco WinEraser Enterprise Volume Edition",
        "Blancco WinEraser Volume Edition for ARM",
        "Blancco WinEraser Enterprise Volume Edition for ARM",
    ],
    // Not present in the software response
    bvme: [],
    bead: ["Blancco Eraser for Apple Devices"],
};

export interface ProductTag {
    productId: string;
    productKey: string;
}

export function extractProductTags(content: string): ProductTag[] {
    return [...content.matchAll(PRODUCT_TAG_PATTERN)].map((match) => ({ productId: match[1], productKey: match[2] }));
}

export function findFirstProductTagIndex(content: string): number {
    return content.search(PRODUCT_TAG_PATTERN);
}

export type ResolvedProduct = Record<string, SoftwarePackage[]> | undefined;

function explodeVersions(softwarePackages: SoftwarePackage[]): SoftwarePackage[] {
    return softwarePackages.flatMap((softwarePackage) =>
        softwarePackage.versions.map((version) => ({ ...softwarePackage, versions: [version] }))
    );
}

function findMatchedProducts(tag: ProductTag, softwarePackages: SoftwarePackage[]): SoftwarePackage[] {
    const expectedProductNames = PRODUCT_KEY_TO_PRODUCT_NAMES[tag.productKey] ?? [];
    return softwarePackages.filter(
        (softwarePackage) =>
            expectedProductNames.includes(softwarePackage.productName) &&
            softwarePackage.licenseIds.includes(tag.productId)
    );
}

export function resolveProducts(
    productTags: ProductTag[],
    softwarePackages: SoftwarePackage[]
): Map<string, ResolvedProduct> {
    const resolved = new Map<string, ResolvedProduct>();
    const shownProductKeys = new Set<string>();

    productTags.forEach((productTag) => {
        const key = `${productTag.productId}:${productTag.productKey}`;
        if (resolved.has(key)) {
            return;
        }

        if (shownProductKeys.has(productTag.productKey)) {
            resolved.set(key, undefined);
            return;
        }
        shownProductKeys.add(productTag.productKey);

        const matchedProducts = Array.from(
            new Set(
                productTags
                    .filter((matchingTag) => matchingTag.productKey === productTag.productKey)
                    .flatMap((matchingTag) => findMatchedProducts(matchingTag, softwarePackages))
            )
        );
        if (matchedProducts.length === 0) {
            resolved.set(key, undefined);
            return;
        }

        const productName = getGroupName(matchedProducts[0]);

        resolved.set(key, { [productName]: explodeVersions(matchedProducts) });
    });

    return resolved;
}
